import React from 'react';
import { Box, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import { Check } from '@mui/icons-material';

/** The one palette for colour-coded settings (grades, leave types, event categories). */
export const SWATCHES = [
  '#22c55e', '#3b82f6', '#f97316', '#ef4444', '#a855f7',
  '#14b8a6', '#eab308', '#ec4899', '#6366f1', '#64748b',
];

/** A palette colour for the nth new item, so items added together differ. */
export const swatchAt = (n: number) => SWATCHES[((n % SWATCHES.length) + SWATCHES.length) % SWATCHES.length];

const HEX = /^#[0-9a-f]{6}$/i;

/** Pick a colour from the palette, or type any hex with `custom`. */
const ColorSwatchPicker: React.FC<{
  value: string;
  onChange: (color: string) => void;
  custom?: boolean;
  disabled?: boolean;
}> = ({ value, onChange, custom = false, disabled }) => (
  <Stack spacing={1.5}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {SWATCHES.map(c => {
        const on = value?.toLowerCase() === c;
        return (
          <Tooltip key={c} title={c}>
            <Box
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => onChange(c)}
              aria-label={`Colour ${c}`}
              aria-pressed={on}
              sx={{
                all: 'unset',
                width: 28,
                height: 28,
                borderRadius: '8px',
                bgcolor: c,
                cursor: disabled ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: on ? `0 0 0 2px var(--mui-palette-background-paper, #fff), 0 0 0 4px ${c}` : 'none',
                transition: 'transform 120ms ease',
                '&:hover': { transform: disabled ? 'none' : 'scale(1.08)' },
                '&:focus-visible': { outline: `2px solid ${c}`, outlineOffset: 2 },
              }}
            >
              {on && <Check sx={{ fontSize: 16, color: '#fff' }} />}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
    {custom && (
      <TextField
        size="small"
        label="Custom colour"
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        error={!!value && !HEX.test(value)}
        helperText={!!value && !HEX.test(value) ? 'Use a hex colour like #22c55e' : ' '}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: HEX.test(value) ? value : 'transparent', border: 1, borderColor: 'divider' }} />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 220 }}
      />
    )}
  </Stack>
);

export default ColorSwatchPicker;
